import React, { useState, useEffect } from 'react';
import {
    Container,
    Typography,
    Box,
    Card,
    CardContent,
    Switch,
    FormControlLabel,
    Grid,
    TextField,
    Button,
    Alert,
    Divider,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    IconButton,
    Tooltip,
    Paper,
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction
} from '@mui/material';
import {
    Save as SaveIcon,
    Schedule as ScheduleIcon,
    Block as BlockIcon,
    Delete as DeleteIcon,
    Add as AddIcon,
    Info as InfoIcon,
    CalendarToday as CalendarIcon,
    AccessTime as TimeIcon
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import moment from 'moment-timezone';
import { toast } from 'react-toastify';
import * as appointmentSettingsActions from '../../../actions/seller/seller.appointmentSettings';
import AccessTimeFilledIcon from '@mui/icons-material/AccessTimeFilled';
import HeaderBack from "components/elements/main/HeaderBack";
import CustomSectionCreator from "components/CustomSectionCreator";

const AppointmentSettings = (props) => {
    const dispatch = useDispatch();

    const {history } = props;

    // Get appointment settings from Redux state
    const {
        settings,
        availableDates,
        loading,
        saving,
        availableDatesLoading,
        error,
        saveError,
        availableDatesError,
        saveSuccess,
        blockDateSuccess,
        unblockDateSuccess
    } = useSelector(state => state.appointmentSettings || {});
    
    // Local state for UI with default values
    const [localSettings, setLocalSettings] = useState(settings || {
        appointment_enabled: false,
        business_hours: {
            monday: { enabled: true, start_time: "09:00", end_time: "17:00" },
            tuesday: { enabled: true, start_time: "09:00", end_time: "17:00" },
            wednesday: { enabled: true, start_time: "09:00", end_time: "17:00" },
            thursday: { enabled: true, start_time: "09:00", end_time: "17:00" },
            friday: { enabled: true, start_time: "09:00", end_time: "17:00" },
            saturday: { enabled: false, start_time: "09:00", end_time: "17:00" },
            sunday: { enabled: false, start_time: "09:00", end_time: "17:00" }
        },
        slot_duration: 30,
        slots_per_time: 2,
        advance_booking_days: 30,
        auto_confirm: false,
        cancellation_policy: {
            cancellation_hours_before: 24
        },
        blocked_dates: []
    });
    const [blockDateDialog, setBlockDateDialog] = useState(false);
    const [selectedBlockDate, setSelectedBlockDate] = useState(null);
    const [blockReason, setBlockReason] = useState('');
    
    const dayNames = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
    const dayLabels = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
    
    useEffect(() => {
        dispatch(appointmentSettingsActions.getAppointmentSettings());
        dispatch(appointmentSettingsActions.getAvailableDates());
    }, [dispatch]);
    
    // Sync Redux settings with local state
    useEffect(() => {
        if (settings) {
            setLocalSettings(settings);
        }
    }, [settings]);
    
    // Handle success/error states
    useEffect(() => {
        if (saveSuccess) {
            toast.success('Appointment settings saved successfully');
            dispatch(appointmentSettingsActions.clearAppointmentSettingsErrors());
        }
    }, [saveSuccess, dispatch]);
    
    useEffect(() => {
        if (blockDateSuccess) {
            toast.success('Date blocked successfully');
            // Refresh both settings and available dates
            dispatch(appointmentSettingsActions.getAppointmentSettings());
            dispatch(appointmentSettingsActions.getAvailableDates());
            setBlockDateDialog(false);
            setSelectedBlockDate(null);
            setBlockReason('');
            dispatch(appointmentSettingsActions.clearAppointmentSettingsErrors());
        }
    }, [blockDateSuccess, dispatch]);
    
    useEffect(() => {
        if (unblockDateSuccess) {
            toast.success('Date unblocked successfully');
            // Refresh both settings and available dates
            dispatch(appointmentSettingsActions.getAppointmentSettings());
            dispatch(appointmentSettingsActions.getAvailableDates());
            dispatch(appointmentSettingsActions.clearAppointmentSettingsErrors());
        }
    }, [unblockDateSuccess, dispatch]);
    
    useEffect(() => {
        if (error) {
            toast.error(error.message || 'Error loading appointment settings');
        }
        if (saveError) {
            toast.error(saveError.message || 'Error saving appointment settings');
        }
        if (availableDatesError) {
            toast.error(availableDatesError.message || 'Error loading available dates');
        }
    }, [error, saveError, availableDatesError]);
    
    const handleSaveSettings = () => {
        dispatch(appointmentSettingsActions.updateAppointmentSettings(localSettings));
    };
    
    const handleBusinessHourChange = (day, field, value) => {
        setLocalSettings(prev => ({
            ...prev,
            business_hours: {
                ...prev?.business_hours,
                [day]: {
                    ...prev?.business_hours?.[day],
                    [field]: value
                }
            }
        }));
    };
    
    const handleBlockDate = () => {
        if (!selectedBlockDate) return;
        dispatch(appointmentSettingsActions.addBlockedDate(selectedBlockDate.toISOString(), blockReason));
    };
    
    const handleUnblockDate = (date) => {
        dispatch(appointmentSettingsActions.removeBlockedDate(date));
    };
    
    const generateTimeOptions = () => {
        const options = [];
        for (let hour = 0; hour < 24; hour++) {
            for (let minute = 0; minute < 60; minute += 30) {
                const timeString = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
                const displayTime = moment(timeString, 'HH:mm').format('h:mm A');
                options.push({ value: timeString, label: displayTime });
            }
        }
        return options;
    };
    
    const timeOptions = generateTimeOptions();
    
    if (loading) {
        return (
            <Container maxWidth="lg" sx={{ py: 4 }}>
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                    <Typography>Loading appointment settings...</Typography>
                </Box>
            </Container>
        );
    }
    
    return (
        <Container>
            <HeaderBack history={history} />
            <CustomSectionCreator isSubtle={true} icon={<AccessTimeFilledIcon />} title="Video Call Settings" />
            <Box mb={2}>
                <Typography variant="subtitle1" color="textSecondary">
                    Configure your appointment booking system
                </Typography>
            </Box>
            
            {/* Main Enable/Disable Toggle */}
            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                        <Box>
                            <Typography variant="h6" gutterBottom>
                                Booking
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                                Enable or disable appointment booking for your store
                            </Typography>
                        </Box>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={localSettings?.appointment_enabled || false}
                                    onChange={(e) => setLocalSettings(prev => ({
                                        ...prev,
                                        appointment_enabled: e.target.checked
                                    }))}
                                    color="primary"
                                />
                            }
                            label={localSettings?.appointment_enabled ? "Enabled" : "Disabled"}
                        />
                    </Box>
                </CardContent>
            </Card>
            
            {localSettings?.appointment_enabled && (
                <>
                    {/* Business Hours Configuration */}
                    <Card sx={{ mb: 3 }}>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                <TimeIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                                Business Hours
                            </Typography>
                            <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                                Set your available days and hours for appointments
                            </Typography>
                            
                            <Grid container spacing={2}>
                                {dayNames.map((day, index) => (
                                    <Grid item xs={12} key={day}>
                                        <Paper sx={{ p: 2 }}>
                                            <Grid container spacing={2} alignItems="center">
                                                <Grid item xs={12} sm={2}>
                                                    <FormControlLabel
                                                        control={
                                                            <Switch
                                                                checked={localSettings?.business_hours?.[day]?.enabled || false}
                                                                onChange={(e) => handleBusinessHourChange(day, 'enabled', e.target.checked)}
                                                                color="primary"
                                                            />
                                                        }
                                                        label={dayLabels[index]}
                                                    />
                                                </Grid>
                                                {localSettings?.business_hours?.[day]?.enabled && (
                                                    <>
                                                        <Grid item xs={12} sm={4}>
                                                            <FormControl fullWidth>
                                                                <InputLabel>Start Time</InputLabel>
                                                                <Select
                                                                    value={localSettings?.business_hours?.[day]?.start_time || "09:00"}
                                                                    onChange={(e) => handleBusinessHourChange(day, 'start_time', e.target.value)}
                                                                    label="Start Time"
                                                                >
                                                                    {timeOptions.map(option => (
                                                                        <MenuItem key={option.value} value={option.value}>
                                                                            {option.label}
                                                                        </MenuItem>
                                                                    ))}
                                                                </Select>
                                                            </FormControl>
                                                        </Grid>
                                                        <Grid item xs={12} sm={4}>
                                                            <FormControl fullWidth>
                                                                <InputLabel>End Time</InputLabel>
                                                                <Select
                                                                    value={localSettings?.business_hours?.[day]?.end_time || "17:00"}
                                                                    onChange={(e) => handleBusinessHourChange(day, 'end_time', e.target.value)}
                                                                    label="End Time"
                                                                >
                                                                    {timeOptions.map(option => (
                                                                        <MenuItem key={option.value} value={option.value}>
                                                                            {option.label}
                                                                        </MenuItem>
                                                                    ))}
                                                                </Select>
                                                            </FormControl>
                                                        </Grid>
                                                    </>
                                                )}
                                            </Grid>
                                        </Paper>
                                    </Grid>
                                ))}
                            </Grid>
                        </CardContent>
                    </Card>
                    
                    {/* Appointment Configuration */}
                    <Card sx={{ mb: 3 }}>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                <ScheduleIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                                Appointment Configuration
                            </Typography>
                            
                            <Grid container spacing={3}>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Slot Duration (minutes)"
                                        type="number"
                                        value={localSettings?.slot_duration || 30}
                                        onChange={(e) => setLocalSettings(prev => ({
                                            ...prev,
                                            slot_duration: parseInt(e.target.value) || 30
                                        }))}
                                        inputProps={{ min: 15, max: 120, step: 15 }}
                                        helperText="Duration of each appointment slot"
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Slots per Time"
                                        type="number"
                                        value={localSettings?.slots_per_time || 2}
                                        onChange={(e) => setLocalSettings(prev => ({
                                            ...prev,
                                            slots_per_time: parseInt(e.target.value) || 1
                                        }))}
                                        inputProps={{ min: 1, max: 10 }}
                                        helperText="Maximum appointments per time slot"
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Advance Booking Days"
                                        type="number"
                                        value={localSettings?.advance_booking_days || 30}
                                        onChange={(e) => setLocalSettings(prev => ({
                                            ...prev,
                                            advance_booking_days: parseInt(e.target.value) || 30
                                        }))}
                                        inputProps={{ min: 1, max: 90 }}
                                        helperText="How many days in advance customers can book"
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Cancellation Hours Before"
                                        type="number"
                                        value={localSettings?.cancellation_policy?.cancellation_hours_before || 24}
                                        onChange={(e) => setLocalSettings(prev => ({
                                            ...prev,
                                            cancellation_policy: {
                                                ...prev.cancellation_policy,
                                                cancellation_hours_before: parseInt(e.target.value) || 24
                                            }
                                        }))}
                                        inputProps={{ min: 1, max: 168 }}
                                        helperText="Hours before appointment for cancellation"
                                    />
                                </Grid>
                            </Grid>
                            
                            <Box mt={3}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={localSettings?.auto_confirm || false}
                                            onChange={(e) => setLocalSettings(prev => ({
                                                ...prev,
                                                auto_confirm: e.target.checked
                                            }))}
                                            color="primary"
                                        />
                                    }
                                    label="Auto-confirm appointments"
                                />
                                <Typography variant="body2" color="textSecondary" sx={{ ml: 4 }}>
                                    Automatically confirm appointments without manual approval
                                </Typography>
                            </Box>
                        </CardContent>
                    </Card>
                    
                    {/* Blocked Dates Management */}
                    <Card sx={{ mb: 3 }}>
                        <CardContent>
                            <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                                <Typography variant="h6">
                                    <BlockIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                                    Blocked Dates
                                </Typography>
                                <Button
                                    variant="contained"
                                    startIcon={<AddIcon />}
                                    onClick={() => setBlockDateDialog(true)}
                                >
                                    Block Date
                                </Button>
                            </Box>
                            
                            {localSettings?.blocked_dates && localSettings.blocked_dates.length > 0 ? (
                                <List>
                                    {localSettings.blocked_dates.map((blockedDate, index) => (
                                        <ListItem key={index} divider>
                                            <ListItemText
                                                primary={moment(blockedDate.date).format('MMMM Do, YYYY')}
                                                secondary={blockedDate.reason || 'No reason provided'}
                                            />
                                            <ListItemSecondaryAction>
                                                <IconButton
                                                    edge="end"
                                                    onClick={() => handleUnblockDate(blockedDate.date)}
                                                    color="error"
                                                >
                                                    <DeleteIcon />
                                                </IconButton>
                                            </ListItemSecondaryAction>
                                        </ListItem>
                                    ))}
                                </List>
                            ) : (
                                <Alert severity="info">
                                    No blocked dates. Click "Block Date" to prevent bookings on specific days.
                                </Alert>
                            )}
                        </CardContent>
                    </Card>
                </>
            )}
            
            {/* Save Button */}
            <Box display="flex" justifyContent="center" mt={4}>
                <Button
                    variant="contained"
                    size="large"
                    startIcon={<SaveIcon />}
                    onClick={handleSaveSettings}
                    disabled={saving}
                >
                    {saving ? 'Saving...' : 'Save Settings'}
                </Button>
            </Box>
            
            {/* Block Date Dialog */}
            <Dialog open={blockDateDialog} onClose={() => setBlockDateDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Block Date</DialogTitle>
                <DialogContent>
                    <Box mb={3}>
                        <Typography variant="subtitle2" gutterBottom>
                            Select Date to Block
                        </Typography>
                        <DatePicker
                            selected={selectedBlockDate}
                            onChange={(date) => setSelectedBlockDate(date)}
                            minDate={new Date()}
                            maxDate={moment().add(localSettings?.advance_booking_days || 30, 'days').toDate()}
                            dateFormat="MMMM d, yyyy"
                            customInput={<TextField fullWidth />}
                            placeholderText="Select a date to block"
                        />
                    </Box>
                    
                    <TextField
                        fullWidth
                        label="Reason (optional)"
                        multiline
                        rows={3}
                        value={blockReason}
                        onChange={(e) => setBlockReason(e.target.value)}
                        placeholder="Why is this date blocked?"
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setBlockDateDialog(false)}>Cancel</Button>
                    <Button 
                        onClick={handleBlockDate} 
                        variant="contained" 
                        disabled={!selectedBlockDate}
                    >
                        Block Date
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

export default AppointmentSettings;
